import type { PropsWithChildren, ReactNode } from 'react';
import React, { useEffect } from 'react';

import { Navigate, useLocation, useMatch } from 'react-router';

import { iots, pipe } from '@code-expert/prelude';
import { useDeepCompareMemoize } from '@code-expert/react';
import { isProduction } from '/imports/config/client';
import type { UserCapability, UserPublic } from '/imports/domain';
import { checkCapabilities, isExamUser, isLTIUser } from '/imports/domain';
import { formatRoute } from '/imports/modules/Routing';
import type { SavedEntryElements } from '/imports/ui/components/GlobalContext';
import {
  useGlobalContext,
  useGlobalContextWithActions,
} from '/imports/ui/components/GlobalContext';
import { UserInboxContextProvider } from '/imports/ui/components/Messages/UserInboxContext';
import { NotAuthorized } from '/imports/ui/components/NotFound';
import { examLogoutRoute, examStudentRoute } from '/imports/ui/pages/exam/routes';
import { pushLastLocation } from '/imports/ui/pages/login/utils';
import { UiSessionState } from '/imports/utils/UiState';

export const uiSessionStates = {
  isExamSession: UiSessionState.valueProp('is-exam-session', iots.boolean),
};

function RedirectToLogin() {
  const location = useLocation();
  pushLastLocation(location);
  if (uiSessionStates.isExamSession.get() === true) {
    uiSessionStates.isExamSession.clear();
    return <Navigate to={formatRoute(examLogoutRoute)({})} />;
  }
  // FIXME Use formatRoute (cx-1895)
  return <Navigate to={isProduction ? '/login' : '/loginDemo'} />;
}

function Unauthorized({ user }: { user: UserPublic }) {
  useEffect(() => {
    if (user != null && isLTIUser(user)) {
      void fetch('/logout', { method: 'POST' });
    }
  }, [user]);
  if (user != null) {
    if (isExamUser(user)) {
      const { examId } = user;
      return <Navigate to={formatRoute(examStudentRoute)({ examId })} />;
    }
  }
  return <NotAuthorized />;
}

// -------------------------------------------------------------------------------------------------

interface PrivateRouteProps {
  allowCapabilities?: Array<UserCapability>;
  denyCapabilities?: Array<UserCapability>;
  layout?: React.ComponentType<React.PropsWithChildren>;
  onMatch?: (params: Record<SavedEntryElements, string>) => void;
  children: ReactNode | ((_: UserPublic) => ReactNode);
}

export function PrivateRoute({
  allowCapabilities: allow,
  denyCapabilities: deny,
  layout: Layout = React.Fragment,
  onMatch,
  children,
}: PrivateRouteProps) {
  const params = pipe(
    useMatch('/:path?/:semester?/:courseUrl?/:groupId?/*')?.params,
    (p) => [p],
    useDeepCompareMemoize,
    (l) => l?.[0] as Record<SavedEntryElements, string>,
  );

  const [{ user, userCapabilities }, dispatch] = useGlobalContextWithActions();
  const isAuthorized = user != null && checkCapabilities({ allow, deny }, userCapabilities);

  React.useEffect(() => {
    if (isAuthorized) {
      if (onMatch) {
        onMatch(params);
      }
    } else {
      if (user != null) {
        // Access to the preferred entry route is restricted, so we remove it.
        dispatch({ preferredEntry: undefined });
      }
    }
  }, [isAuthorized, onMatch, dispatch, params, user]);

  return user != null ? (
    <UserInboxContextProvider>
      <Layout>
        {isAuthorized ? (
          typeof children === 'function' ? (
            children(user)
          ) : (
            children
          )
        ) : (
          <Unauthorized user={user} />
        )}
      </Layout>
    </UserInboxContextProvider>
  ) : (
    <RedirectToLogin />
  );
}

// -------------------------------------------------------------------------------------------------

interface PublicRouteProps {
  allowCapabilities?: Array<UserCapability>;
  denyCapabilities?: Array<UserCapability>;
  layout?: React.ComponentType<React.PropsWithChildren>;
  onMatch?: (params: Record<SavedEntryElements, string>) => void;
}

export function PublicRoute({
  layout: Layout = React.Fragment,
  onMatch,
  children,
}: PropsWithChildren<PublicRouteProps>) {
  const { user } = useGlobalContext();
  const params = useMatch('/:path?/:semester?/:courseUrl?/:groupId?')?.params;
  const isAuthenticated = user != null;

  React.useEffect(() => {
    if (!isAuthenticated && onMatch) onMatch(params as Record<SavedEntryElements, string>);
  }, [isAuthenticated, onMatch, params]);

  return isAuthenticated ? (
    <PrivateRoute layout={Layout} onMatch={onMatch}>
      {children}
    </PrivateRoute>
  ) : (
    <Layout>{children}</Layout>
  );
}
